import { isEmpty } from 'lodash';
import React from 'react';
import { compose } from 'redux';
import { withProps } from 'recompose';
import { withRouter } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';

import * as ENUM from '@dpdgroupuk/redback-enums';
import { withPageControl } from '@dpdgroupuk/fmx-ui/components/Page';
import { IMAGES } from '@dpdgroupuk/fmx-ui/themes/constants';
import Wizard, { Step } from '@dpdgroupuk/fmx-ui/components/Wizard';
import { withModal } from '@dpdgroupuk/fmx-ui/components/Dialog';

import type { AuthorizedParcel } from '../../../../types';
import SelectDayStep from './SelectDayStep';
import DeliveryPreferencesStep from './DeliveryPreferencesStep';
import SelectSafePlace from './SelectSafePlace';
import CompleteStep from './CompleteStep';
import {
  SAFE_PLACE_DELIVERY_OPTION,
  trackable,
} from '../../../../constants/analytics';
import {
  whatHappensNextSpeedDeal,
  doSomethingDifferentSpeedDeal,
  trackAnotherParcelHeader,
  backToDpdSiteHeader,
  backToGreenSiteHeader,
  getDpdAppHeader,
  whatHappensNextHeader,
  goBackToDeliveryDetailsSpeedDeal,
  doSomethingDifferentHeader,
} from '../../../controlActions';
import * as MESSAGES from '../../../../constants/message';
import withParcel from '../../../../HOCs/withParcel';
import { fetchActionsDatesByCode } from '../../actions';
import { SFP_STEPS } from '../../steps';
import { withDeliveryUpdate } from '../../hocs';
import { changeDeliverToSafePlace } from './actions';
import { isCreateProfilePossible } from '../../../../models/parcel';
import GetContactDetailsStep from '../../components/GetContactDetailsStep';
import withFetch from '../../../../HOCs/withFetch';
import EnterCodeStep from '../../components/EnterCodeStep';
import GetContactDetailsNoEmailStep from '../../components/GetContactDetailsNoEmailStep';

const useStyles = makeStyles(({ typography, breakpoints }) => ({
  completeStep: {
    paddingLeft: typography.pxToRem(120),
    paddingRight: typography.pxToRem(120),
    [breakpoints.down('xs')]: {
      paddingLeft: typography.pxToRem(15),
      paddingRight: typography.pxToRem(15),
    },
  },
}));

type Props = {
  parcel: AuthorizedParcel,
  dates: Array<string>,
  updateDelivery: Function,
  onSubmitSuccess: Function,
  onSubmitFailure: Function,
};

const SafePlaceComponent = ({
  parcel,
  dates,
  updateDelivery,
  onSubmitSuccess,
  onSubmitFailure,
}: Props) => {
  const styles = useStyles();

  return (
    <Wizard
      initialStep={
        isEmpty(dates) ? SFP_STEPS.SELECT_SAFE_PLACE : SFP_STEPS.SELECT_DAY
      }
      onSubmit={updateDelivery}
      onSubmitSuccess={onSubmitSuccess}
      onSubmitFailure={onSubmitFailure}
    >
      <Step
        stepId={SFP_STEPS.SELECT_DAY}
        dates={dates}
        component={SelectDayStep}
      />
      <Step
        stepId={SFP_STEPS.SELECT_SAFE_PLACE}
        component={SelectSafePlace}
        isCreateProfilePossible={isCreateProfilePossible(parcel)}
        parcel={parcel}
      />

      {/* Auth */}
      <Step
        stepId={SFP_STEPS.GET_CONTACTS}
        component={GetContactDetailsNoEmailStep}
        parcel={parcel}
        nextStep={SFP_STEPS.ENTER_CODE}
      />

      <Step
        stepId={SFP_STEPS.ENTER_CODE}
        component={EnterCodeStep}
        parcel={parcel}
      />
      {/* End Auth */}

      <Step
        stepId={SFP_STEPS.GET_CONTACTS_NO_AUTH}
        component={GetContactDetailsStep}
        parcel={parcel}
      />

      <Step
        stepId={SFP_STEPS.DELIVERY_PREFERENCES}
        component={DeliveryPreferencesStep}
        updateDelivery={updateDelivery}
      />
      <Step
        stepId={SFP_STEPS.COMPLETE}
        component={CompleteStep}
        parcel={parcel}
        actionCode={ENUM.PARCEL_ACTION_CODE.SFP}
        wrapperClassName={styles.completeStep}
      />
    </Wizard>
  );
};

const createSpeedDealControlActions = props => [
  doSomethingDifferentSpeedDeal(props),
  goBackToDeliveryDetailsSpeedDeal(props),
  whatHappensNextSpeedDeal(props),
];

const createHeaderControlActions = props => [
  doSomethingDifferentHeader(props),
  trackAnotherParcelHeader(props),
  whatHappensNextHeader(props),
  backToDpdSiteHeader(props),
  backToGreenSiteHeader(props),
  getDpdAppHeader(props),
];

const SafePlace = compose(
  withRouter,
  withParcel(),
  withModal,
  withFetch(({ match }) =>
    fetchActionsDatesByCode(match.params.parcelId, ENUM.PARCEL_ACTION_CODE.SFP)
  ),
  withProps(props => ({
    dates: props.fetchedData,
    onSubmitSuccess: ({ goToStep }) => goToStep(SFP_STEPS.COMPLETE),
  })),
  withDeliveryUpdate({
    actionCode: ENUM.PARCEL_ACTION_CODE.SFP,
    changeDeliveryAction: changeDeliverToSafePlace,
    trackable: SAFE_PLACE_DELIVERY_OPTION.ON_SUBMIT,
  }),
  withPageControl({
    title: MESSAGES.DELIVER_TO_SAFE_PLACE,
    backgroundImage: IMAGES.MAP,
    trackable: trackable(SAFE_PLACE_DELIVERY_OPTION),
    speedDealActions: createSpeedDealControlActions,
    headerMenuActions: createHeaderControlActions,
  })
)(SafePlaceComponent);

export default SafePlace;
